import { useState } from 'react'

import {
  Box,
  Text,
  WizardStepRenderParams,
  dimensions,
} from '@factorialco/deprecated-design-system'
import { type FiltersDefinition, type GroupingDefinition } from '@factorialco/f0-react'
import {
  type DataCollectionBaseFetchOptions,
  type DataCollectionPaginatedFetchOptions,
  type ItemActionsDefinition,
  type NavigationFiltersDefinition,
  OneDataCollection,
  type SortingsDefinition,
  type SummariesDefinition,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import { Pencil, Reset } from '@factorialco/f0-react/icons/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { useActiveMarkets } from 'modules/benefits/hooks/useActiveMarkets'
import {
  MEALS_LIMIT_EUR_SPAIN,
  getAvailableCategories,
  getMarketLimits,
} from 'modules/benefits/lib/supportedMarkets'
import { MEALS_CATEGORY, TRANSPORTATION_CATEGORY } from 'modules/benefits/lib/transactionCategories'
import {
  SpendingLimitOverride,
  WizardData,
} from 'modules/benefits/pages/EmployeesPage/ActivateBenefitsModal/types'

import { BulkEditSpendingLimitModal } from './BulkEditSpendingLimitModal'
import { BulkResetSpendingLimitModal } from './BulkResetSpendingLimitModal'

export const BenefitsSpendingLimitOverrideStep = ({
  data,
  onDataChanged,
}: WizardStepRenderParams<WizardData>) => {
  const { activeMarkets } = useActiveMarkets()

  //  Assuming that the first market is the primary market we will to change this when we support multiple markets
  const currentMarket = activeMarkets[0] || 'es'
  const rawMarketLimits = getMarketLimits(currentMarket)
  const availableCategories = useMemo(() => getAvailableCategories(currentMarket), [currentMarket])

  const marketLimits = {
    meals: rawMarketLimits.meals ?? MEALS_LIMIT_EUR_SPAIN,
    transportation: rawMarketLimits.transportation ?? 0,
  }

  const [spendingLimits, setSpendingLimits] = useState<Record<string, number>>(() => {
    const initialLimits: Record<string, number> = {}

    Object.keys(data.employee_details_map).forEach((employeeIdStr) => {
      const employeeId = Number(employeeIdStr)
      if (availableCategories.includes('meals')) {
        initialLimits[`${employeeId}-${MEALS_CATEGORY}`] =
          rawMarketLimits.meals ?? MEALS_LIMIT_EUR_SPAIN
      }
      if (availableCategories.includes('transportation') && rawMarketLimits.transportation) {
        initialLimits[`${employeeId}-${TRANSPORTATION_CATEGORY}`] = rawMarketLimits.transportation
      }
    })

    return initialLimits
  })

  const [bulkEditModal, setBulkEditModal] = useState<{
    isOpen: boolean
    selectedItems: SpendingLimitOverride[]
  }>({
    isOpen: false,
    selectedItems: [],
  })

  const [resetModal, setResetModal] = useState<{
    isOpen: boolean
    selectedItems: SpendingLimitOverride[]
  }>({
    isOpen: false,
    selectedItems: [],
  })

  const { access } = useCurrentSession()
  const accessLocale = access?.locale

  const handleBulkEdit = (selectedItems: SpendingLimitOverride[], newLimit: number) => {
    const updatedLimits = { ...spendingLimits }

    selectedItems.forEach((item) => {
      const key = `${item.employeeId}-${item.category}`
      updatedLimits[key] = newLimit
    })

    setSpendingLimits(updatedLimits)
    onDataChanged({
      ...data,
      spending_limits: updatedLimits,
    })
  }

  const openBulkEditModal = (selectedItems: SpendingLimitOverride[]) => {
    setBulkEditModal({
      isOpen: true,
      selectedItems,
    })
  }

  const closeBulkEditModal = () => {
    setBulkEditModal({
      isOpen: false,
      selectedItems: [],
    })
  }

  const handleBulkSave = (newLimit: number) => {
    handleBulkEdit(bulkEditModal.selectedItems, newLimit)
    closeBulkEditModal()
  }

  const openBulkResetModal = (selectedItems: SpendingLimitOverride[]) => {
    setResetModal({
      isOpen: true,
      selectedItems,
    })
  }

  const closeBulkResetModal = () => {
    setResetModal({
      isOpen: false,
      selectedItems: [],
    })
  }

  const handleBulkReset = () => {
    const updatedLimits = { ...spendingLimits }
    resetModal.selectedItems.forEach((item) => {
      if (item.category === MEALS_CATEGORY) {
        updatedLimits[`${item.employeeId}-${MEALS_CATEGORY}`] =
          rawMarketLimits.meals ?? MEALS_LIMIT_EUR_SPAIN
      } else if (item.category === TRANSPORTATION_CATEGORY && rawMarketLimits.transportation) {
        updatedLimits[`${item.employeeId}-${TRANSPORTATION_CATEGORY}`] =
          rawMarketLimits.transportation
      }
    })
    setSpendingLimits(updatedLimits)
    onDataChanged({
      ...data,
      spending_limits: updatedLimits,
    })
    closeBulkResetModal()
  }

  const employeesWithSpendingLimits: SpendingLimitOverride[] = useUnsafeMemo(
    () =>
      Object.entries(data.employee_details_map).flatMap(([employeeIdStr, employeeDetails]) => {
        const employeeId = employeeIdStr
        const limits = []

        if (availableCategories.includes('meals')) {
          limits.push({
            id: `${employeeId}-${MEALS_CATEGORY}`,
            employeeId,
            employeeName: `${employeeDetails.firstName} ${employeeDetails.lastName}`,
            employeeAvatar: employeeDetails.avatarUrl,
            amountCents: Math.round(spendingLimits[`${employeeId}-${MEALS_CATEGORY}`] ?? 0),
            category: MEALS_CATEGORY,
          })
        }

        if (availableCategories.includes('transportation')) {
          limits.push({
            id: `${employeeId}-${TRANSPORTATION_CATEGORY}`,
            employeeId,
            employeeName: `${employeeDetails.firstName} ${employeeDetails.lastName}`,
            employeeAvatar: employeeDetails.avatarUrl,
            amountCents: Math.round(
              spendingLimits[`${employeeId}-${TRANSPORTATION_CATEGORY}`] ?? 0
            ),
            category: TRANSPORTATION_CATEGORY,
          })
        }

        return limits
      }),
    [data.employee_details_map, availableCategories, spendingLimits]
  )

  const getEmployeesToResetLimits = () => {
    return employeesWithSpendingLimits.filter((item) => {
      const defaultAmountCents =
        item.category === MEALS_CATEGORY
          ? rawMarketLimits.meals
          : item.category === TRANSPORTATION_CATEGORY
            ? (rawMarketLimits.transportation ?? 0)
            : 0

      return item.amountCents !== defaultAmountCents
    })
  }

  const filterOptions = []
  if (availableCategories.includes('meals')) {
    filterOptions.push({
      label: i18n.t('benefits.homepage.static_benefits.es.meals.title'),
      value: MEALS_CATEGORY,
    })
  }
  if (availableCategories.includes('transportation')) {
    filterOptions.push({
      label: i18n.t('benefits.homepage.static_benefits.es.transport.title'),
      value: TRANSPORTATION_CATEGORY,
    })
  }

  const presets = []
  if (availableCategories.includes('meals')) {
    presets.push({
      label: i18n.t('benefits.homepage.static_benefits.es.meals.title'),
      filter: { category: [MEALS_CATEGORY] },
    })
  }
  if (availableCategories.includes('transportation')) {
    presets.push({
      label: i18n.t('benefits.homepage.static_benefits.es.transport.title'),
      filter: { category: [TRANSPORTATION_CATEGORY] },
    })
  }

  const defaultCategory = availableCategories.includes('meals')
    ? MEALS_CATEGORY
    : availableCategories[0]

  const source = useDataCollectionSource<
    SpendingLimitOverride,
    FiltersDefinition<string>,
    SortingsDefinition,
    SummariesDefinition,
    ItemActionsDefinition<SpendingLimitOverride>,
    NavigationFiltersDefinition<string>,
    GroupingDefinition<SpendingLimitOverride>
  >(
    {
      filters: {
        category: {
          type: 'in',
          label: i18n.t(
            'benefits.tabs.employees.activation_results.modal.edit_spending_limit.category_generic_title'
          ),
          options: {
            options: filterOptions,
          },
        },
      },
      primaryActions: () => ({
        label: i18n.t(
          'benefits.tabs.employees.activation_results.modal.edit_spending_limit.reset_limits_action_title'
        ),
        icon: Reset,
        onClick: () => openBulkResetModal(getEmployeesToResetLimits()),
      }),
      currentFilters: {
        category: [defaultCategory],
      },
      presets,
      selectable: (item: SpendingLimitOverride) => String(item.id),
      bulkActions: () => ({
        primary: [
          {
            label: i18n.t(
              'benefits.tabs.employees.activation_results.modal.edit_spending_limit.edit_spending_limit_edit_limits_action_title'
            ),
            icon: Pencil,
            id: 'edit-limits',
          },
        ],
      }),
      search: {
        enabled: true,
        sync: true,
      },
      dataAdapter: {
        fetchData: (
          options:
            | DataCollectionBaseFetchOptions<
                FiltersDefinition<string>,
                NavigationFiltersDefinition<string>
              >
            | DataCollectionPaginatedFetchOptions<
                FiltersDefinition<string>,
                NavigationFiltersDefinition<string>
              >
        ) => {
          let filteredUsers = [...employeesWithSpendingLimits]

          // Apply filters
          if (
            options.filters?.category &&
            Array.isArray(options.filters.category) &&
            options.filters.category.length > 0
          ) {
            const categoryFilter = options.filters.category
            filteredUsers = filteredUsers.filter((user) => categoryFilter.includes(user.category))
          }

          // Apply search
          if (options.search) {
            const searchLower = options.search.toLowerCase()
            filteredUsers = filteredUsers.filter(
              (user) =>
                user.employeeName.toLowerCase().includes(searchLower) ||
                user.category.toLowerCase().includes(searchLower)
            )
          }

          // Apply pagination
          const currentPage = 'pagination' in options ? options.pagination?.currentPage || 1 : 1
          const perPage = 10
          const start = (currentPage - 1) * perPage
          const end = start + perPage
          const paginatedData = filteredUsers.slice(start, end)

          return Promise.resolve({
            records: paginatedData,
            total: filteredUsers.length,
            currentPage,
            perPage,
            pagesCount: Math.ceil(filteredUsers.length / perPage),
            type: 'pages',
          })
        },
        perPage: 10,
        paginationType: 'pages',
      },
    },
    [employeesWithSpendingLimits]
  )

  return (
    <Box flexDirection="column" position="relative" width="full">
      <Box flexDirection="column" paddingBottom="s20" width="full">
        <Text.Normal weight="semibold" color="grey1000" size="400">
          {i18n.t(
            'benefits.tabs.employees.activation_results.modal.edit_spending_limit.step_title'
          )}
        </Text.Normal>

        <Box paddingBottom="s12">
          <Text.Normal weight="normal" color="grey800" size="300">
            {i18n.t(
              'benefits.tabs.employees.activation_results.modal.edit_spending_limit.introduction_text'
            )}
          </Text.Normal>
        </Box>
        {/* Padding offsets dataCollection margin */}
        <Box width="full" style={{ paddingLeft: dimensions.s24, paddingRight: dimensions.s24 }}>
          <OneDataCollection
            source={source}
            onBulkAction={(
              action: string,
              selectedItems: {
                itemsStatus: readonly { checked: boolean; item: SpendingLimitOverride }[]
              }
            ) => {
              const items = selectedItems.itemsStatus
                .filter((item) => item.checked)
                .map((item) => item.item)

              switch (action) {
                case 'edit-limits':
                  openBulkEditModal(items)
                  break
              }
            }}
            visualizations={[
              {
                type: 'table',
                options: {
                  columns: [
                    {
                      label: i18n.t('common.employee'),
                      render: (item: SpendingLimitOverride) => {
                        return {
                          type: 'person',
                          value: {
                            firstName: item.employeeName?.split(' ')[0] || '',
                            lastName: item.employeeName?.split(' ').slice(1).join(' ') || '',
                            src: item.employeeAvatar || undefined,
                          },
                        }
                      },
                      sorting: 'employeeName',
                    },
                    {
                      label: i18n.t(
                        'benefits.tabs.employees.activation_results.modal.edit_spending_limit.spending_limit_generic_title'
                      ),
                      render: (item: SpendingLimitOverride) => {
                        const amountCents = Number(item.amountCents ?? 0)
                        return MoneyWithCurrency({
                          amount: amountCents,
                          currency: 'EUR',
                          locale: accessLocale,
                        })
                      },
                      sorting: 'amountCents',
                      align: 'right',
                    },
                    {
                      label: i18n.t(
                        'benefits.tabs.employees.activation_results.modal.edit_spending_limit.category_generic_title'
                      ),
                      render: (item: SpendingLimitOverride) => {
                        return String(item.category)
                      },
                      sorting: 'category',
                    },
                  ],
                },
              },
            ]}
          />
        </Box>
      </Box>

      <BulkEditSpendingLimitModal
        isOpen={bulkEditModal.isOpen}
        selectedItems={bulkEditModal.selectedItems}
        onClose={closeBulkEditModal}
        onSave={handleBulkSave}
        defaultLimits={marketLimits}
      />

      <BulkResetSpendingLimitModal
        isOpen={resetModal.isOpen}
        selectedItems={resetModal.selectedItems}
        onClose={closeBulkResetModal}
        onSave={handleBulkReset}
        defaultLimits={marketLimits}
      />
    </Box>
  )
}

export const validateBenefitsSpendingLimitOverrideStep = (data: WizardData): boolean => {
  return Object.keys(data.employee_details_map).length > 0
}
